import { Button, Div, Label, Loader, PageSlider, Slider, Touchable } from "@grassrootly/rn-core";
import { RootStackScreenProps } from "../../navigation/Navigation.types";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import React from "react";
import GameSectionVote from "./GameSectionVote";
import { useGameVoteTraining } from "./useGameVoteTraining";
import { VoteType } from "@grassrootly/api";
import { Platform } from "react-native";

/**
 * GameVoteTrainingScreen - Voting interface for TRAINING games with opponent/teammate split
 * 
 * Six-tab interface (opponent first, then teammate for each category):
 * - Tab 1: Vote for best opponent Playmaker (Creativity)
 * - Tab 2: Vote for best teammate Playmaker (Creativity)
 * - Tab 3: Vote for best opponent Defender (Defense)
 * - Tab 4: Vote for best teammate Defender (Defense)
 * - Tab 5: Vote for best opponent Workhorse (Effort)
 * - Tab 6: Vote for best teammate Workhorse (Effort)
 * 
 * Vote types: "creativity", "defense", "effort" (same as before, but 6 votes total)
 */
export default function GameVoteTrainingScreen({ route, navigation }: RootStackScreenProps<"vote">) {
  const insents = useSafeAreaInsets();
  const { game, opponents, teammates, submitVotes, saving, goBack } = useGameVoteTraining(
    route.params.id,
    navigation
  );

  const [tabIndex, setTabIndex] = React.useState(0);
  const pageRef = React.useRef<Slider>(null);

  // Opponent votes
  const [opponentCreativity, setOpponentCreativity] = React.useState<number | null>(null);
  const [opponentDefense, setOpponentDefense] = React.useState<number | null>(null);
  const [opponentEffort, setOpponentEffort] = React.useState<number | null>(null);

  // Teammate votes
  const [teammateCreativity, setTeammateCreativity] = React.useState<number | null>(null);
  const [teammateDefense, setTeammateDefense] = React.useState<number | null>(null);
  const [teammateEffort, setTeammateEffort] = React.useState<number | null>(null);

  const handleSubmit = () => {
    const votes = [
      { type: VoteType.CREATIVITY, attendanceId: opponentCreativity },
      { type: VoteType.CREATIVITY, attendanceId: teammateCreativity },
      { type: VoteType.DEFENSE, attendanceId: opponentDefense },
      { type: VoteType.DEFENSE, attendanceId: teammateDefense },
      { type: VoteType.EFFORT, attendanceId: opponentEffort },
      { type: VoteType.EFFORT, attendanceId: teammateEffort },
    ];
    submitVotes(votes);
  };

  return (
    <Div
      classNames={[
        "flex1",
        "bgNeutralDarkerDark",
        "bwTop-{1}",
        "borderPrimary",
        "p16",
        `pb-{${insents.bottom + 16 + (Platform.OS == "web" ? 16 : 0)}}`,
        "h100",
      ]}
    >
      {!game ? (
        <Loader size={"large"} />
      ) : (
        <Div classNames={["flex1", "maxW-{920}", "w-{100%}", "mr-{auto}", "ml-{auto}"]}>
          <Touchable classNames={["ml-{auto}"]} onPress={goBack}>
            <Label type="l2Regular" classNames={["colorPrimaryMain"]}>
              Later
            </Label>
          </Touchable>
          <Div classNames={["flex1", "mt-{24}"]}>
            <Label type="l1Regular" classNames={["textCenter"]}>
              The Game has finished.
            </Label>
            <Label type="l1Regular" classNames={["textCenter", "mb-{36}"]}>
              Let's see who was the best player!
            </Label>

            <PageSlider
              classNames={["flex1", "mb-{24}"]}
              initialPageIndex={tabIndex}
              onPageChange={setTabIndex}
              slider={pageRef}
              slides={[
                // Tab 1: Opponent Creativity
                <GameSectionVote
                  key="opponent-creativity"
                  title="Best Opponent Playmaker (Creativity)"
                  attendees={opponents}
                  onSelected={setOpponentCreativity}
                  selectedId={opponentCreativity}
                  hostTeamName={game.host_team_name}
                  hostTeamLogo={game.host_team_logo}
                  visitorTeamName={game.visitor_team_name}
                  visitorTeamLogo={game.visitor_team_logo}
                />,
                // Tab 2: Teammate Creativity
                <GameSectionVote
                  key="teammate-creativity"
                  title="Best Teammate Playmaker (Creativity)"
                  attendees={teammates}
                  onSelected={setTeammateCreativity}
                  selectedId={teammateCreativity}
                  hostTeamName={game.host_team_name}
                  hostTeamLogo={game.host_team_logo}
                  visitorTeamName={game.visitor_team_name}
                  visitorTeamLogo={game.visitor_team_logo}
                />,
                // Tab 3: Opponent Defense
                <GameSectionVote
                  key="opponent-defense"
                  title="Best Opponent Defender"
                  attendees={opponents}
                  onSelected={setOpponentDefense}
                  selectedId={opponentDefense}
                  hostTeamName={game.host_team_name}
                  hostTeamLogo={game.host_team_logo}
                  visitorTeamName={game.visitor_team_name}
                  visitorTeamLogo={game.visitor_team_logo}
                />,
                // Tab 4: Teammate Defense
                <GameSectionVote
                  key="teammate-defense"
                  title="Best Teammate Defender"
                  attendees={teammates}
                  onSelected={setTeammateDefense}
                  selectedId={teammateDefense}
                  hostTeamName={game.host_team_name}
                  hostTeamLogo={game.host_team_logo}
                  visitorTeamName={game.visitor_team_name}
                  visitorTeamLogo={game.visitor_team_logo}
                />,
                // Tab 5: Opponent Effort
                <GameSectionVote
                  key="opponent-effort"
                  title="Best Opponent Workhorse (Effort)"
                  attendees={opponents}
                  onSelected={setOpponentEffort}
                  selectedId={opponentEffort}
                  hostTeamName={game.host_team_name}
                  hostTeamLogo={game.host_team_logo}
                  visitorTeamName={game.visitor_team_name}
                  visitorTeamLogo={game.visitor_team_logo}
                />,
                // Tab 6: Teammate Effort
                <GameSectionVote
                  key="teammate-effort"
                  title="Best Teammate Workhorse (Effort)"
                  attendees={teammates}
                  onSelected={setTeammateEffort}
                  selectedId={teammateEffort}
                  hostTeamName={game.host_team_name}
                  hostTeamLogo={game.host_team_logo}
                  visitorTeamName={game.visitor_team_name}
                  visitorTeamLogo={game.visitor_team_logo}
                />,
              ]}
            />

            {/* Page Indicators */}
            <Div classNames={["flexRow", "alignItemsCenter", "justifyContentCenter", "mv-{8}", "mt-{auto}", "mb-{24}"]}>
              {[0, 1, 2, 3, 4, 5].map((indicator, i) => (
                <Touchable
                  onPress={() => pageRef.current?.setPage(i)}
                  key={i}
                  classNames={[
                    "w-{13}",
                    "h-{3}",
                    "p-{2}",
                    "br-{3}",
                    "mh-{5}",
                    `opacity-{${tabIndex == i ? "1" : "0.2"}}`,
                    tabIndex == i ? "bgPrimaryMain" : "bgColor-{lightgray}",
                  ]}
                />
              ))}
            </Div>

            {/* Action Buttons */}
            {tabIndex === 5 ? (
              // Last tab: Show Back and Submit
              <Div classNames={["flexRow", "gap-{8}"]}>
                <Button
                  title="Back"
                  classNames={["btn", "btnSecondary", "flex1"]}
                  onPress={() => pageRef.current?.setPage(tabIndex - 1)}
                />
                <Button
                  title="Submit Votes"
                  classNames={["btn", "btnPrimary", "flex1"]}
                  isLoading={saving}
                  disabled={saving}
                  onPress={handleSubmit}
                />
              </Div>
            ) : (
              // Other tabs: Show Next button
              <Button
                title="Next"
                classNames={[
                  "btn", 
                  "btnPrimary", 
                  "btnBlock",
                  ((tabIndex === 0 && !opponentCreativity) || 
                   (tabIndex === 1 && !teammateCreativity) || 
                   (tabIndex === 2 && !opponentDefense) || 
                   (tabIndex === 3 && !teammateDefense) || 
                   (tabIndex === 4 && !opponentEffort)) ? "opacity-{0.5}" : ""
                ]}
                onPress={() => pageRef.current?.setPage(tabIndex + 1)}
                disabled={
                  (tabIndex === 0 && !opponentCreativity) || 
                  (tabIndex === 1 && !teammateCreativity) || 
                  (tabIndex === 2 && !opponentDefense) || 
                  (tabIndex === 3 && !teammateDefense) || 
                  (tabIndex === 4 && !opponentEffort)
                }
              />
            )}
          </Div>
        </Div>
      )}
    </Div>
  );
}
